import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LuArrowRight, LuCheck } from "react-icons/lu";
import HeaderOne from "../../header/HeaderOne";
import FooterOne from "../../footer/FooterOne";
import Head from "../../components/Head";
import "./GhlPromo.css";

const ADVANTAGES = [
  {
    title: "One SIM. Many locations.",
    body: "Connect multiple GHL sub-accounts to a single SIM card. Competitors like mycrmsim.com force a new SIM per location — more hardware, more cost, more hassle."
  },
  {
    title: "No 10DLC. No A2P.",
    body: "P2P messages through your own SIM. Skip the 2–6 weeks of carrier registration that Twilio + every aggregator forces you into."
  },
  {
    title: "No per-message fees.",
    body: "GHL doesn't bill per message when TextLink is the provider. Your only cost is your SIM's flat monthly rate — usually $5–$25/month for unlimited SMS."
  }
];

const STEPS = [
  {
    n: 1,
    title: "Sign up for TextLink",
    body: "Create your TextLink account at textlinksms.com/signup. 300 free messages, no credit card required.",
    time: "1 min"
  },
  {
    n: 2,
    title: "Install the TextLink app on an Android phone",
    body: "Download our Android app, scan the QR code in your dashboard, and the phone pairs automatically. Any spare Android works — even a $60 used Galaxy A.",
    time: "3 min"
  },
  {
    n: 3,
    title: "Install TextLink in your GHL agency",
    body: "Open the GoHighLevel marketplace, search 'TextLink', and click Install. Authorize the sub-accounts you want to send SMS through. We register as a native Conversation Provider — replies appear in GHL's unified inbox automatically.",
    time: "30 sec"
  },
  {
    n: 4,
    title: "Connect each sub-account",
    body: "For each GHL sub-account: copy that location's API key from GHL (Settings → Business Profile → API Key) and paste it into the matching TextLink sub-user. This binds the GHL location to the SIMs assigned to that sub-user.",
    time: "1 min per location"
  },
  {
    n: 5,
    title: "Send & receive in GHL",
    body: "That's it. Outbound messages from the GHL UI route through your SIM. Replies land in GHL's unified inbox. Delivery status syncs back automatically.",
    time: "Instant"
  }
];

const COMPARE = [
  { feature: "Per-message cost",        textlink: "$0.001–$0.005 (your SIM's plan)", twilio: "$0.0079+ per SMS" },
  { feature: "10DLC / A2P registration", textlink: "Not required",                   twilio: "Required, 2–6 weeks" },
  { feature: "GHL per-message fees",     textlink: "$0",                              twilio: "$0.015 per segment" },
  { feature: "Setup time",               textlink: "Under 5 minutes",                 twilio: "Days to weeks" },
  { feature: "Multiple GHL sub-accounts on one SIM", textlink: "Yes",                 twilio: "N/A (no SIM)" },
  { feature: "Two-way messaging",        textlink: "Yes",                             twilio: "Yes" }
];

export default function GhlPromo() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="GoHighLevel SMS Provider — Native GHL Integration"
        description="Native Conversation Provider for GoHighLevel. Send SMS through your own SIM at $0.001 per message — no per-message fees from your CRM, no 10DLC paperwork. Connect multiple sub-accounts to one SIM."
        canonical="/ghl"
      />
      <HeaderOne />

      <main className="gp">
        <div className="gp-shell">

          {/* ---- HERO ---- */}
          <header className="gp-hero">
            <span className="gp-eyebrow">GoHighLevel integration</span>
            <h1 className="gp-h1">
              Native SMS in GoHighLevel.<br />
              <span className="gp-h1-accent">Your SIM. Your phone. Your messages.</span>
            </h1>
            <p className="gp-lead">
              TextLink is a native Conversation Provider in GHL. Connect your sub-accounts in under 5 minutes and start sending SMS for cents instead of paying Twilio's premium — with no 10DLC paperwork.
            </p>
            <div className="gp-cta-row">
              <button className="gp-btn gp-btn--primary" onClick={() => navigate('/signup')}>
                Start free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <a className="gp-btn gp-btn--ghost" href="https://youtu.be/Fiv1HMRunDM" target="_blank" rel="noopener noreferrer">
                Watch the 5-min walkthrough
              </a>
            </div>
            <div className="gp-meta">
              <span>300 free messages</span>
              <span className="gp-meta-sep" />
              <span>No credit card</span>
              <span className="gp-meta-sep" />
              <span>Cancel anytime</span>
            </div>
          </header>

          {/* ---- WHY TEXTLINK ---- */}
          <section className="gp-section">
            <h2 className="gp-h2">Why agencies pick TextLink for GHL</h2>
            <p className="gp-sub">Three things competitors don't get right.</p>
            <div className="gp-grid-3">
              {ADVANTAGES.map((a, i) => (
                <div key={i} className="gp-card">
                  <h3 className="gp-card-h">{a.title}</h3>
                  <p className="gp-card-p">{a.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ---- CONNECTION FLOW ---- */}
          <section className="gp-section">
            <h2 className="gp-h2">How to connect — step by step</h2>
            <p className="gp-sub">
              Whole flow takes about 5 minutes for the first sub-account. Each additional sub-account is ~1 minute.
            </p>

            <ol className="gp-steps">
              {STEPS.map((s) => (
                <li key={s.n} className="gp-step">
                  <div className="gp-step-num">{s.n}</div>
                  <div className="gp-step-body">
                    <div className="gp-step-head">
                      <h3 className="gp-step-title">{s.title}</h3>
                      <span className="gp-step-time">{s.time}</span>
                    </div>
                    <p className="gp-step-text">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="gp-callout">
              <strong>That's the whole connection flow.</strong> Once a sub-account is bound, GHL's UI talks to TextLink automatically — your team uses GHL exactly like they would with Twilio, but the messages route through your SIM card and replies land in GHL's unified inbox.
            </div>
          </section>

          {/* ---- DEMO VIDEO ---- */}
          <section className="gp-section">
            <h2 className="gp-h2">See it in action</h2>
            <p className="gp-sub">Full setup walkthrough — under 6 minutes start to finish.</p>
            <div className="gp-video">
              <iframe
                src="https://www.youtube.com/embed/Fiv1HMRunDM"
                title="TextLink + GoHighLevel setup"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>

          {/* ---- COMPARISON TABLE ---- */}
          <section className="gp-section">
            <h2 className="gp-h2">TextLink vs Twilio (for GHL)</h2>
            <p className="gp-sub">The difference matters most at scale.</p>
            <div className="gp-table-wrap">
              <table className="gp-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>TextLink</th>
                    <th>Twilio (typical)</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((row, i) => (
                    <tr key={i}>
                      <td className="gp-feat">{row.feature}</td>
                      <td className="gp-good">
                        <LuCheck size={14} strokeWidth={2.5} /> {row.textlink}
                      </td>
                      <td className="gp-bad">{row.twilio}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ---- FINAL CTA ---- */}
          <section className="gp-cta">
            <h2 className="gp-h2">Ready to ditch Twilio?</h2>
            <p className="gp-sub">Five minutes from signup to first SMS sent through GHL.</p>
            <div className="gp-cta-row">
              <button className="gp-btn gp-btn--primary" onClick={() => navigate('/signup')}>
                Start free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="gp-btn gp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
